import React from "react";
import { PiX, PiMinus, PiCaretUpDownFill } from "react-icons/pi";
import SyntaxHighlighter from "react-syntax-highlighter";
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { getComplementaryColor } from "../utils/colors";
import { RGBColor } from "react-color";
import { useNavigate } from "react-router-dom";
import { removeLeadingWhitespace } from "../utils/snippets";

export default function SnippetFrame({
  snippet,
  setSnippet,
  selectedLanguage,
  loadedSnippet,
  snippetRef,
  selectedLines,
  setSelectedLines,
  hoveredLine,
  setHoveredLine,
  showLineNumbers,
  showBackground,
  setShowBackground,
  screenshotTitle,
  setScreenshotTitle,
  backgroundColor,
  hexColor,
  clearSnippet,
  loading,
  converter,
}: {
  snippet: string;
  setSnippet: React.Dispatch<React.SetStateAction<string>>;
  selectedLanguage: string;
  loadedSnippet: boolean;
  snippetRef: React.RefObject<HTMLTextAreaElement>;
  selectedLines: number[];
  setSelectedLines: React.Dispatch<React.SetStateAction<number[]>>;
  hoveredLine: number | null;
  setHoveredLine: React.Dispatch<React.SetStateAction<number | null>>;
  showLineNumbers: boolean;
  showBackground: boolean;
  setShowBackground: React.Dispatch<React.SetStateAction<boolean>>;
  screenshotTitle: string;
  setScreenshotTitle: React.Dispatch<React.SetStateAction<string>>;
  backgroundColor: RGBColor;
  hexColor: string;
  clearSnippet: () => void;
  loading: boolean;
  converter: boolean;
}) {
  const navigate = useNavigate();

  const rgbaToCssColor = (rgba: RGBColor) => {
    return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
  };

  return (
    <div
      className={`flex justify-center  ${
        converter ? "w-1/2" : " w-full p-0 md:p-8"
      } bg-neutral/80 rounded-md `}
    >
      <div
        className={`${showBackground && !converter ? "py-8 md:p-8" : ""} ${
          converter ? "" : " resize-x "
        } overflow-x-hidden  w-full`}
        style={{ backgroundColor: converter ? "transparent" : hexColor }}
        id="screenshot-container"
      >
        <div
          className={`bg-neutral w-full h-full  ${
            showBackground && !converter
              ? "rounded-none md:rounded-lg shadow-xl shadow-black/50"
              : "rounded-lg"
          }`}
        >
          <div className="flex items-center w-full">
            <div
              className={`w-32 h-12 pt-2 px-6 gap-2 flex flex-row items-center`}
            >
              <div
                title="Clear Snippet"
                className="rounded-full bg-neutral-content/30 hover:bg-red-500  hover:text-black/50 text-transparent border-none 
              h-4 w-4 shadow-inner cursor-pointer place-content-center align-middle"
                onClick={() => {
                  if (snippet && !loading) {
                    clearSnippet();
                    navigate("/snippet/new");
                  } else {
                    clearSnippet();
                  }
                }}
              >
                <center>
                  <PiX size={12} />
                </center>
              </div>
              <div
                title="Remove Leading Spaces"
                className="rounded-full bg-neutral-content/30 hover:bg-yellow-500 hover:text-black/50 text-transparent border-none 
              h-4 w-4 shadow-inner cursor-pointer place-content-center align-middle"
                onClick={() => {
                  if (snippet && !loading) {
                    const formatted = removeLeadingWhitespace(snippet);
                    setSnippet(formatted);
                  }
                }}
              >
                <center>
                  <PiMinus size={12} />
                </center>
              </div>
              <div
                title="Remove Leading Spaces"
                className="rounded-full bg-neutral-content/30 hover:bg-green-500 hover:text-black/50 text-transparent border-none 
              h-4 w-4 shadow-inner cursor-pointer place-content-center align-middle"
                onClick={() => {
                  setShowBackground(!showBackground);
                }}
              >
                <center className="-rotate-45">
                  <PiCaretUpDownFill size={12} />
                </center>
              </div>
            </div>
            <div className="w-full flex justify-center ">
              <input
                id="screenshot-title"
                title="Screenshot Snippet"
                placeholder="Untitled"
                value={screenshotTitle}
                onChange={(e) => setScreenshotTitle(e.target.value)}
                type="text"
                className="w-11/12 pt-5 bg-transparent p-2 font-mono text-center text-neutral-content/50 leading-normal outline-none text-ellipsis"
              ></input>
            </div>
            <div className="w-32"></div>
          </div>
          <div
            tabIndex={0}
            onKeyDown={() => snippetRef.current?.focus()}
            onClick={() => snippetRef.current?.focus()}
            className={`relative flex w-full ${
              converter ? "text-base " : "text-xs md:text-sm "
            } ${showLineNumbers ? "" : "-translate-x-5"}`}
          >
            {(!loadedSnippet || converter) && (
              <textarea
                title="Code Snippet"
                placeholder="Enter your code snippet here..."
                className={`absolute inset-0 resize-none bg-transparent p-2 font-mono text-transparent w-full 
                   caret-white leading-normal outline-none overflow-x-scroll no-scrollbar ml-[46px] pl-0 whitespace-pre`}
                ref={snippetRef}
                value={snippet}
                spellCheck={false}
                onChange={(e) => setSnippet(e.target.value)}
              />
            )}
            <div className="w-full">
              <SyntaxHighlighter
                showLineNumbers={true}
                lineNumberStyle={{
                  padding: "0 8px",
                  width: "38px",
                  color: showLineNumbers ? "" : "transparent",
                }}
                wrapLines
                //wrapLongLines
                language={selectedLanguage}
                style={atomOneDark}
                customStyle={{
                  flex: "1",
                  background: "transparent",
                  borderRadius: "6px", // adjust this value as needed
                  minHeight: "40px",
                  scrollbarWidth: "none" /* For Firefox */,
                  msOverflowStyle: "none" /* For Internet Explorer and Edge */,
                  overflowX: "scroll",
                  cursor: "text",
                }}
                lineProps={(lineNumber) => {
                  return {
                    style: {
                      backgroundColor: selectedLines.includes(lineNumber)
                        ? rgbaToCssColor(
                            getComplementaryColor(backgroundColor, 0.3)
                          )
                        : hoveredLine === lineNumber
                        ? rgbaToCssColor(getComplementaryColor(backgroundColor))
                        : "",
                      marginLeft:
                        !showLineNumbers &&
                        (selectedLines.includes(lineNumber) ||
                          hoveredLine === lineNumber)
                          ? "16px"
                          : "",
                    },
                    onClick: () => {
                      setSelectedLines((prev) => {
                        if (prev.includes(lineNumber)) {
                          return prev.filter((line) => line !== lineNumber);
                        } else {
                          return [...prev, lineNumber];
                        }
                      });
                    },
                    onMouseEnter: () => {
                      setHoveredLine(lineNumber);
                    },
                    onMouseLeave: () => setHoveredLine(null),
                  };
                }}
              >
                {snippet}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
